import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { html, TemplateResult } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { Editor } from '@toast-ui/editor';
import { newStandardEditor } from '../../../webmodule-common/other/ui/toastui';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { AddConversationAttachment, ViewConversationEntry } from '../../api/dealer-api-interface-franchisee';
import { WebModuleConversationEntryDropZone } from './wm-conversation-entry-dropzone';
import { LangDealerFranchiseeConversationWmConversationEntry as Lang } from '../../../localization/scripts/dealer-franchisee/conversation/wm-conversation-entry-lang';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { SupportTicketService } from './support-ticket-service';
import { debounce } from '../../../webmodule-common/other/common/helpers/callbacks';

export type SupportConversationEditorMode = 'create' | 'edit';

export interface SupportConversationEditorCreateDetail {
  ticketId: string;
  entryId: string; // newly generated
  text: string;
  draft: boolean;
  attachments: AddConversationAttachment[];
}
export interface SupportConversationEditorUpdateDetail {
  ticketId: string;
  entryId: string; // existing
  text: string;
  draft: boolean; // draft retained or promoted to final
  attachments: AddConversationAttachment[];
  delete?: boolean;
}
export interface SupportConversationEditorCancelDetail {
  mode: SupportConversationEditorMode;
}

@customElement('wm-support-conversation-entry-editor')
export class WMSupportConversationEntryEditor extends LitElementBase {
  @property({ type: String }) ticketId = '';
  @property({ type: String }) mode: SupportConversationEditorMode = 'create';
  @property({ attribute: false })
  private _entry?: ViewConversationEntry | undefined; // supplied in edit mode
  public get entry(): ViewConversationEntry | undefined {
    return this._entry;
  }
  public set entry(value: ViewConversationEntry | undefined) {
    this._entry = value;
  }
  /** Quote manager required; absence is a programming error */
  @property({ attribute: false }) quoteManager: any;
  @property({ type: Boolean }) allowDraft = true;
  @property({ type: Boolean }) allowCancel = true;
  @property({ type: Boolean }) autoRotateIdOnCreate = true;

  @state() private isUploading = false;
  @query('#textEditor') textEditor?: HTMLElement;
  private editor?: Editor;
  @query('#attachments') private attachmentsEditor?: WebModuleConversationEntryDropZone;

  // Render into light DOM so surrounding styles can target internal elements directly.
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this; // disable shadow root so global styles apply
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  protected firstUpdated(): void {
    if (this.textEditor && !this.editor) {
      this.editor = newStandardEditor(this.textEditor, undefined, 145);
      this.applyEntryContent();
      // Hook per-keystroke draft sync when editing a draft or creating one (silent service draft update)
      (this.editor as any).on('change', () => this._debouncedSyncDraftText());
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('entry') && this.mode === 'edit') {
      this.applyEntryContent();
    }
    // If mode transitioned back to 'create' (e.g., draft deleted or final posted), clear editor & attachments
    if (changed.has('mode')) {
      const previousMode = changed.get('mode');
      if (previousMode === 'edit' && this.mode === 'create') {
        // Fresh create state: rely on session draftEntry id; do not locally rotate
        this.applyEntryContent();
      }
      if (this.mode === 'create' && previousMode === 'create') {
        // Edge: editingEntry cleared without mode change event (unlikely) – still ensure blank
        this.applyEntryContent();
      }
    }
  }

  private applyEntryContent() {
    if (!this.editor) return;
    if (this.mode === 'edit' && this.entry) {
      this.editor.setMarkdown(this.entry.text ?? '');
      this.attachmentsEditor?.resetAttachments(this.entry.attachments ?? []);
      // Persisted edit mode uses entry.id directly; no local id tracking needed
    } else if (this.mode === 'create') {
      this.editor.setMarkdown('');
      this.attachmentsEditor?.resetAttachments([]);
    }
    // After content initialization ensure service draft state reflects editor text for drafts
    this._syncDraftTextImmediate();
  }

  private gatherAttachments(): AddConversationAttachment[] {
    return this.attachmentsEditor?.allFilesAsInputs ?? [];
  }

  private async ensureContent(): Promise<boolean> {
    const text = this.editor?.getMarkdown() ?? '';
    if (isEmptyOrSpace(text)) {
      await information(Lang.pleaseAddContentToComment());
      return false;
    }
    return true;
  }

  private async onPost(draft: boolean) {
    if (this.isUploading) return; // prevent double submit
    if (!this.ticketId) {
      // Cannot post without an associated ticket id
      await information(Lang.pleaseAddContentToComment()); // reuse dialog infra; could add distinct message
      return;
    }
    if (!(await this.ensureContent())) return;
    try {
      this.isUploading = true; // reuse flag to disable buttons
      const text = this.editor?.getMarkdown() ?? '';
      const attachments = this.gatherAttachments();
      if (this.mode === 'create') {
        // Draft create path: ensure draftEntry exists then update/save
        const service = this._requireService();
        try {
          const session = service.getSession(this.ticketId);
          if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
        } catch {
          /* ignore */
        }
        service.updateDraftConversationEntry(this.ticketId, { text, attachments });
        await service.saveTicket(this.ticketId, { finalizeDraftEntry: !draft });
        if (!draft) {
          // Final post: reset editor; next keystroke will create a new draftEntry via ensureDraftEntry
          this.resetCreate();
        }
      } else if (this.mode === 'edit' && this.entry) {
        // Call service directly for update/save
        const service = this._requireService();
        service.updateDraftConversationEntry(this.ticketId, { text, attachments });
        await service.saveTicket(this.ticketId, { finalizeDraftEntry: !draft });
      }
    } finally {
      this.isUploading = false;
    }
  }

  private resetCreate() {
    if (this.mode !== 'create') return;
    this.editor?.setMarkdown('');
    this.attachmentsEditor?.resetAttachments([]);
  }

  private onDelete = async () => {
    if (this.mode !== 'edit' || !this.entry) return;
    try {
      this.isUploading = true; // prevent double clicks
      // Call service directly for delete
      const service = this._requireService();
      // Replace discard call with unified save using deleteDraftEntry option
      await service.saveTicket(this.ticketId, { deleteDraftEntry: true });
    } finally {
      this.isUploading = false;
    }
  };

  // ---- Draft Sync Helpers ----
  private _debouncedSyncDraftText = debounce(() => this._syncDraftTextImmediate(), 300);
  private _syncDraftTextImmediate() {
    // Only sync if draft editing or create mode draft creation.
    const isDraftEdit = this.mode === 'edit' && !!this.entry?.draft;
    const isCreateDraft = this.mode === 'create';
    if (!isDraftEdit && !isCreateDraft) return;
    if (!this.ticketId) return;
    const service: SupportTicketService = this._requireService();
    const text = this.editor?.getMarkdown() ?? '';
    // attachments/users sync on explicit add/remove; include attachments for completeness
    const attachments: AddConversationAttachment[] = this.attachmentsEditor?.allFilesAsInputs ?? [];
    try {
      const session = service.getSession(this.ticketId);
      if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
      if (session.draftEntry && session.draftEntry.text === text) {
        const prevNames = (session.draftEntry.attachments || []).map(a => a.filename).join('|');
        const newNames = attachments.map(a => a.filename).join('|');
        if (prevNames === newNames) return; // idempotent skip
      }
      service.updateDraftConversationEntry(this.ticketId, { text, attachments });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('draftSync.failed', err);
    }
  }

  private _requireService(): SupportTicketService {
    const svc = this.quoteManager?.supportTicketService;
    if (!svc)
      throw new Error('SupportTicketService unavailable in WMSupportConversationEntryEditor (quoteManager missing).');
    return svc;
  }

  private onUploadingChanged = (e: CustomEvent<{ isUploading: boolean }>) => {
    this.isUploading = e.detail.isUploading;
    // Attachment list may have changed; force immediate sync (skip debounce)
    this._syncDraftTextImmediate();
  };

  render(): TemplateResult {
    const isEdit = this.mode === 'edit';
    const isDraftEdit = isEdit && !!this.entry?.draft;
    const canSaveDraft = this.allowDraft && (this.mode === 'create' || isDraftEdit);

    // Button policy:
    // Edit draft: Delete, Save Draft, Post
    // Edit final (non-draft): Post
    // Create: (retain previous) Save Draft (if allowed), Post

    const buttons: TemplateResult[] = [];
    if (isDraftEdit) {
      buttons.push(
        html`<webmodule-button
          size="small"
          variant="default"
          class="ms-1"
          ?disabled=${this.isUploading}
          @click=${this.onDelete}
        >
          <webmodule-icon name="fas-trash-can" library="fa" slot="prefix"></webmodule-icon>
        </webmodule-button>`
      );
    }
    // Cancel removed (external editor is always visible; no hide cycle required)
    if (canSaveDraft) {
      buttons.push(
        html`<webmodule-button
          size="small"
          variant="default"
          class="ms-1"
          ?disabled=${this.isUploading}
          @click=${() => this.onPost(true)}
        >
          ${Lang.saveDraftComment()}
        </webmodule-button>`
      );
    }
    buttons.push(
      html`<webmodule-button
        size="small"
        variant="primary"
        class="ms-1"
        ?disabled=${this.isUploading}
        @click=${() => this.onPost(false)}
      >
        ${Lang.postComment()}
      </webmodule-button>`
    );

    const isCreate = this.mode === 'create';
    const authorSupplier = !isCreate && this.entry && this.entry.systemUserId !== getCurrentUser()?.id;
    const rootBase = isCreate ? 'conversation-entry-create' : 'conversation-entry-create';
    const authorClass = isCreate ? '' : authorSupplier ? ' author-supplier' : ' author-dealer';
    const rootClass = `${rootBase}${authorClass}`;
    return html`<div class="support-conversation-entry-editor ${rootClass}">
      <div class="conversation-text"><div id="textEditor"></div></div>
      <wmview-conversation-attachments
        id="attachments"
        @wm-event-changed=${this.onUploadingChanged}
        .conversationId=${this.ticketId}
        .conversationEntryId=${this.conversationEntryId}
        .existingFiles=${isEdit ? (this.entry?.attachments?.filter(a => !a.embedded) ?? []) : []}
      ></wmview-conversation-attachments>
      <div class="conversation-list-actions">${buttons}</div>
    </div>`;
  }

  private get conversationEntryId(): string {
    if (!this.ticketId) return this.entry?.id || '';
    try {
      const svc = this._requireService();
      const session = svc.getSession(this.ticketId);
      const draftContext = this.mode === 'create' || (this.mode === 'edit' && !!this.entry?.draft);
      if (draftContext) {
        if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
        return session.draftEntry?.id || '';
      }
      return this.entry?.id || '';
    } catch {
      return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-support-conversation-entry-editor': WMSupportConversationEntryEditor;
  }
}
